import AppInfoFooter from "@/components/AppInfoFooter"
import Button from "@/components/Button"
import { EmailInput, PasswordInput } from "@/components/InputFields"
import Link from "@/components/Link"
import Page from "@/components/Page"
import {
  useCheckSignUpInvitationQuery,
  useResendInvitationEmailMutation,
  useSignUpMutation,
} from "@/data/queries"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"

import InputErrorMessage from "@/components/InputErrorMessage"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import { backendErrorCodeFor } from "@/model/BackendErrors"
import { ROUTES, withQuery } from "@/routes"
import { updatePasswordSchema } from "@/schemas/zod-schemas"
import cn from "@/utils/cn"
import { useRawSearchParams } from "@/utils/hooks/useSearchParams"
import { zodResolver } from "@hookform/resolvers/zod"

type SignUpPageSearchParams = {
  token: string
  entityId: string
  username: string
}

type SignUpFormData = {
  password: string
  passwordConfirmation: string
}

const SignUpPage = () => {
  // The BioT signup query string parameters are not URL encoded, so we use the hook that parses them raw
  const {
    token,
    entityId: userId,
    username: email,
  } = useRawSearchParams<SignUpPageSearchParams>()
  const navigate = useNavigate()
  const { register, handleSubmit, formState } = useForm<SignUpFormData>({
    mode: "all",
    defaultValues: { password: "", passwordConfirmation: "" },
    resolver: zodResolver(updatePasswordSchema),
  })

  const {
    isPending: isValidatingInvitation,
    error: invitationValidationError,
  } = useCheckSignUpInvitationQuery({ token, userId })

  const {
    mutateAsync: resendInvitation,
    isPending: isResendingInvitation,
    error: resendInvitationError,
  } = useResendInvitationEmailMutation()

  const {
    mutateAsync: signUp,
    isPending: isSigningUp,
    error: signUpError,
  } = useSignUpMutation()

  const { errors } = formState
  const hasValidInvitation =
    !isValidatingInvitation && invitationValidationError === null
  const isInvitationExpired =
    backendErrorCodeFor(invitationValidationError) ===
    "SIGN_UP_INVITATION_EXPIRED"
  const errorCode = backendErrorCodeFor(
    resendInvitationError ?? invitationValidationError ?? signUpError
  )

  const onSubmit = async (formData: SignUpFormData) => {
    await signUp({ token, userId, password: formData.password })
    navigate(withQuery(ROUTES.POPULATION.ROOT, { protocol: "diabetes" }), {
      replace: true,
    })
  }

  const onResendInvitationSelected = async () => {
    await resendInvitation({ email })
    navigate(ROUTES.RESEND_INVITATION_SUCCESS, { replace: true })
  }

  return (
    <Page className="bg-gray-50">
      <div className="flex h-full flex-col justify-between">
        <div
          className={cn(
            "mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl"
          )}
        >
          <h2 className="mb-3 text-center text-3xl font-bold text-primary/90">
            Welcome to
            <br />
            Bloomlife Connects
          </h2>
          <h3 className="mb-6 text-center text-lg text-primary/90">
            Please create your password below
          </h3>

          {errorCode && (
            <div className="mb-4 mt-8 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
              <span className="material-icons me-4 text-red-600">error</span>
              <p className="text-red-600">
                {errorCode === "SIGN_UP_INVITATION_EXPIRED" ? (
                  "This invitation has expired."
                ) : errorCode === "SIGN_UP_INVITATION_ALREADY_ACCEPTED" ? (
                  <>
                    This invitation has already been accepted.
                    <br />
                    Please&nbsp;
                    <Link to={ROUTES.LOGIN}>log in</Link>.
                  </>
                ) : errorCode === "WEAK_PASSWORD" ? (
                  "The password is commonly used and easily guessed. Please choose a stronger password"
                ) : (
                  "Something went wrong. Please contact the dev team."
                )}
              </p>
            </div>
          )}
          {isValidatingInvitation ? (
            <Spinner className="mt-8" />
          ) : isInvitationExpired ? (
            <Button
              variant="primary"
              type="button"
              disabled={isResendingInvitation}
              className="mt-8 w-full text-base"
              onClick={onResendInvitationSelected}
            >
              Resend invitation
            </Button>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-3"
            >
              <EmailInput
                id="email"
                placeholder="Email address"
                autoComplete="username"
                disabled
                className="hidden disabled:cursor-not-allowed"
              />

              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="password" className="ml-1">
                  New Password
                </InputLabel>
                <PasswordInput
                  id="password"
                  placeholder="New password"
                  autoComplete="new-password"
                  className="disabled:cursor-not-allowed"
                  disabled={isSigningUp || !hasValidInvitation}
                  {...register("password")}
                />

                <InputErrorMessage
                  message={
                    <div>
                      {errors?.password?.message && (
                        <p>The Password does not meet the requirements:</p>
                      )}
                      <ul className="ml-4 mt-1.5 list-disc">
                        <li>Minimum 8 characters</li>
                        <li>At least one uppercase letter (A-Z)</li>
                        <li>At least one lowercase letter (a-z)</li>
                        <li>At least one digit (0-9)</li>
                        <li>
                          At least one special character{" "}
                          <span className="tracking-widest">(!@#$%&*?)</span>
                        </li>
                      </ul>
                    </div>
                  }
                  className={cn(
                    "ml-1",
                    errors?.password?.message
                      ? "text-red-600"
                      : "text-primary/90"
                  )}
                />
              </div>

              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="password-confirmation" className="ml-1">
                  Confirm New Password
                </InputLabel>
                <PasswordInput
                  autoComplete="new-password"
                  id="password-confirmation"
                  placeholder="Confirm new password"
                  className="disabled:cursor-not-allowed"
                  disabled={isSigningUp || !hasValidInvitation}
                  {...register("passwordConfirmation")}
                />
                <InputErrorMessage
                  message={errors?.passwordConfirmation?.message}
                  className="ml-1"
                />
              </div>
              <Button
                variant="primary"
                type="submit"
                disabled={isSigningUp || !hasValidInvitation}
                className="mt-6 flex w-full gap-3 text-base"
              >
                {isSigningUp && <Spinner />}
                Sign up
              </Button>
            </form>
          )}
        </div>
        <AppInfoFooter className="mt-auto p-4" />
      </div>
    </Page>
  )
}

export default SignUpPage
